import type { EditorTarget } from './editor-link';
import { useEffect, useState } from 'react';
import { isOpenEditorSupported, postOpenEditor } from './editor-link';

/**
 * Whether the installed extension can open the editor on a variant, and the call that asks it
 * to. The content script adds its marker when it runs, which can be after the first render, so
 * until it is there the body is watched (its direct children only) for it.
 *
 * `open` must be called from the click handler itself, not after an await: the extension can
 * open its side panel only inside the click's user activation.
 */
export function useEditorLink() {
  const [isSupported, setIsSupported] = useState(() => isOpenEditorSupported(document));

  useEffect(() => {
    if (isSupported || typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => {
      if (!isOpenEditorSupported(document)) return;
      setIsSupported(true);
      observer.disconnect();
    });
    observer.observe(document.body, { childList: true });
    if (isOpenEditorSupported(document)) setIsSupported(true);
    return () => observer.disconnect();
  }, [isSupported]);

  const open = (target: EditorTarget) => postOpenEditor({ target, win: window });

  return { isSupported, open };
}
